import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisYear,
  parseISO,
} from 'date-fns';

/**
 * Format a date string to a human-readable relative format.
 *
 * - "Just now" - within the last minute
 * - "5 minutes ago" - within the last hour
 * - "2 hours ago" - within the last 24 hours
 * - "Yesterday" - if yesterday
 * - "Monday" - if within the last week
 * - "Jan 15" - if this year
 * - "Jan 15, 2023" - if older
 *
 * @param dateString - ISO date string
 * @returns Formatted date string
 *
 * @example
 * formatRelativeDate('2024-01-15T10:30:00Z') // => "2 hours ago"
 * formatRelativeDate('2024-01-14T10:30:00Z') // => "Yesterday"
 */
export function formatRelativeDate(dateString: string): string {
  const date = parseISO(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Within the last minute
  if (diffInSeconds < 60) {
    return 'Just now';
  }

  // Within the last hour - show minutes ago
  if (diffInSeconds < 3600) {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  // Today - show hours ago
  if (isToday(date)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  // Yesterday
  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // This week - show day name
  if (isThisWeek(date, { weekStartsOn: 1 })) {
    return format(date, 'EEEE'); // e.g., "Monday"
  }

  // This year - show month and day
  if (isThisYear(date)) {
    return format(date, 'MMM d'); // e.g., "Jan 15"
  }

  // Older - show full date
  return format(date, 'MMM d, yyyy'); // e.g., "Jan 15, 2023"
}

/**
 * Format a date to a full datetime string
 *
 * @param dateString - ISO date string
 * @returns Formatted datetime string
 *
 * @example
 * formatFullDate('2024-01-15T10:30:00Z') // => "January 15, 2024 at 10:30 AM"
 */
export function formatFullDate(dateString: string): string {
  const date = parseISO(dateString);
  return format(date, "MMMM d, yyyy 'at' h:mm a");
}

/**
 * Format a date for display in a modal or detail view
 *
 * @param dateString - ISO date string
 * @returns Object with date and time strings
 *
 * @example
 * formatDateTime('2024-01-15T10:30:00Z')
 * // => { date: "January 15, 2024", time: "10:30 AM" }
 */
export function formatDateTime(dateString: string): { date: string; time: string } {
  const date = parseISO(dateString);
  return {
    date: format(date, 'MMMM d, yyyy'),
    time: format(date, 'h:mm a'),
  };
}

/**
 * Get the current ISO date string
 *
 * @returns Current date as ISO string
 */
export function getCurrentISODate(): string {
  return new Date().toISOString();
}

/**
 * Check if a date is within the last N days
 *
 * @param dateString - ISO date string
 * @param days - Number of days to check
 * @returns True if the date is within the last N days
 */
export function isWithinDays(dateString: string, days: number): boolean {
  const date = parseISO(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  return diffInDays <= days;
}

/**
 * Sort dates in descending order (newest first)
 *
 * @param a - First ISO date string
 * @param b - Second ISO date string
 * @returns Sort comparison value
 */
export function sortByDateDesc(a: string, b: string): number {
  return parseISO(b).getTime() - parseISO(a).getTime();
}

/**
 * Sort dates in ascending order (oldest first)
 *
 * @param a - First ISO date string
 * @param b - Second ISO date string
 * @returns Sort comparison value
 */
export function sortByDateAsc(a: string, b: string): number {
  return parseISO(a).getTime() - parseISO(b).getTime();
}
